import { useState } from 'react';
import { useMount } from 'react-use';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  NewButton,
} from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import {
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import {
  AppointmentUserMicSettings,
  MicrophoneRequestAlert,
  useSelectedMicrophoneDevice,
} from '@eluve/smart-blocks';

import {
  useTranscriptionMachineActor,
  useTranscriptionMachineSelector,
} from '../hooks/useTranscriptionMachine';

const InnerMicrophoneFailedDialog = () => {
  const [open, setOpen] = useState(true);
  const [selectedMicId, setSelectedMicId] = useSelectedMicrophoneDevice();
  const { send } = useTranscriptionMachineActor();
  const logger = useNamedLogger('MicrophoneFailedDialog');
  const tenantId = useTenantIdFromParams();
  const userId = useUserIdFromSession();

  const machineState = useTranscriptionMachineSelector((state) => state);
  const { microphoneStatus, sessionAppointmentId: appointmentId } =
    machineState.context;

  useMount(() => {
    logger.warn('MicrophoneFailedDialog shown', {
      microphoneStatus,
      appointmentId,
      userId,
      tenantId,
    });
  });

  const onResume = () => {
    send({ type: 'RESUME', deviceId: selectedMicId ?? undefined });
  };

  const onClose = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent close={false} className="max-w-md">
        <DialogHeader className="mb-2 space-y-4">
          <DialogTitle>Your Microphone Got Disconnected</DialogTitle>
          <DialogDescription>
            Recording has been paused. Before resuming ensure the microphone
            selected below is the one you'd like to use.
          </DialogDescription>
          <MicrophoneRequestAlert />
          <AppointmentUserMicSettings
            forceVerticalLayout
            isCompact
            selectedMicId={selectedMicId}
            setSelectedMicId={setSelectedMicId}
            showInputLabel={false}
          />
        </DialogHeader>
        <DialogFooter>
          <NewButton onClick={onClose} text="Stay Paused" type="subtle" />
          <NewButton
            disabled={!selectedMicId}
            onClick={onResume}
            text="Resume Recording"
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MicrophoneFailedDialog = () => {
  const machineState = useTranscriptionMachineSelector((state) => state);
  const hasFailed =
    machineState.can({ type: 'RESUME' }) &&
    machineState.context.microphoneStatus === 'disconnected';

  if (!hasFailed) {
    return null;
  }

  return <InnerMicrophoneFailedDialog />;
};
